/**
 * NOVA's estate status dashboard — the PITG replacement's first surface.
 *
 * Everything on this page is measured: the backend probes each site on a fixed
 * interval and this view renders the newest recorded cycle. Nothing here is
 * seeded or simulated.
 *
 * Board grammar (dashboard design rules): KPI band (L1 strip) → one filter row
 * that scopes everything below it (§4.3, `?range=…&sites=…` in the URL) → the
 * heat rows, the latency trend chart and the site table — and a site drawer as
 * the L2 view, keyed by `?site=<id>`. The filter row offers the 7/30/90-day
 * windows the rollup endpoint can serve (defaulting to the full 90 days) but no
 * custom from/to: `/monitoring/history` only serves "the last N days", so an
 * arbitrary past window would label the wrong period.
 */

import { Icon, RefreshCw } from "@norican/icons";
import { Alert, Button, Freshness, PageHeader, Skeleton, Typography } from "@norican/react";

import { FilterRow, useBoardRange } from "@/components/board";
import { useMonitoringHistory, useMonitoringOverview, useRefreshMonitoring } from "@/lib/api";
import { useDrillParams } from "@/lib/drill";

import { EstateSummary } from "./estate-summary";
import { LatencyTrend } from "./latency-trend";
import { SiteDrawer } from "./site-drawer";
import { SiteTable } from "./site-table";
import { driftTitle, redirectDrift, SITE_FILTER_PARAM } from "./status";
import { UptimeHeatmap } from "./uptime-heatmap";

/** Windows the rollup endpoint can serve, and the one the board opens on. */
const HISTORY_PRESETS = ["7d", "30d", "90d"] as const;
const DEFAULT_HISTORY_PRESET = "90d";

export function DashboardPage() {
  const { data, isPending, error } = useMonitoringOverview();
  const refresh = useRefreshMonitoring();
  // Same default the FilterRow below is given, or the chips and the heat rows
  // would describe different windows.
  const range = useBoardRange(DEFAULT_HISTORY_PRESET);
  const { data: history } = useMonitoringHistory(range.days);
  const drill = useDrillParams(SITE_FILTER_PARAM);
  const selectedIds = drill.getList(SITE_FILTER_PARAM);

  const summary = data?.summary;
  // Treat the poll interval as the staleness threshold, with a little slack —
  // past that, the dot escalates on its own.
  const staleAfterMs = summary ? summary.poll_interval_sec * 1000 * 1.5 : undefined;

  const sites = data?.sites ?? [];
  // Sites whose registered URL redirects elsewhere: what NOVA measures is the
  // redirect, not the host that serves the page. Surfaced, never corrected —
  // changing what is monitored belongs to whoever owns the target list.
  const drifted = sites.flatMap((site) => {
    const drift = redirectDrift(site);
    return drift ? [{ site, drift }] : [];
  });
  const filteredSites =
    selectedIds.length > 0 ? sites.filter((site) => selectedIds.includes(site.id)) : sites;
  const filteredHistorySites =
    selectedIds.length > 0
      ? (history?.sites ?? []).filter((site) => selectedIds.includes(site.id))
      : (history?.sites ?? []);

  return (
    <main className="mx-auto flex max-w-7xl flex-col gap-6 px-6 py-8">
      <PageHeader
        title="Estate status"
        subtitle="Live availability of Norican's business websites, checked from the NOVA backend."
        actions={
          <div className="flex items-center gap-4">
            {summary?.last_checked_at && (
              <Freshness
                label="Checked"
                since={summary.last_checked_at}
                staleAfter={staleAfterMs}
                pulse={refresh.isPending}
              />
            )}
            <Button
              variant="outline"
              tone="neutral"
              startIcon={<Icon icon={RefreshCw} size="sm" />}
              loading={refresh.isPending}
              onClick={() => refresh.mutate()}
            >
              Check now
            </Button>
          </div>
        }
      />

      {error && (
        <Alert tone="danger" title="Couldn't load the estate status">
          {error instanceof Error ? error.message : "The monitoring API did not respond."}
        </Alert>
      )}

      {refresh.isError && (
        <Alert tone="warning" title="That check didn't complete">
          Showing the last successful cycle instead.
        </Alert>
      )}

      {drifted.length > 0 && (
        <Alert
          tone="warning"
          title={`${drifted.length} ${drifted.length === 1 ? "site answers" : "sites answer"} at a different URL`}
        >
          <ul className="flex flex-col gap-1">
            {drifted.map(({ site, drift }) => (
              <Typography variant="body-sm" as="li" key={site.id} title={driftTitle(drift)}>
                {site.name}: {site.url} → {drift.finalUrl} ({drift.hops}{" "}
                {drift.hops === 1 ? "redirect" : "redirects"}
                {drift.schemeChanged ? ", scheme changed" : ""})
              </Typography>
            ))}
          </ul>
          <Typography variant="body-sm" as="p" className="mt-2">
            The checks above measure the registered URL, so a certificate or HTTPS failure at the
            address that actually serves the site would not appear here. NOVA has not changed what
            it monitors — the target list is yours to decide.
          </Typography>
        </Alert>
      )}

      {isPending && !data ? (
        <div className="flex flex-col gap-4" aria-busy="true">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} height="7rem" />
            ))}
          </div>
          <Skeleton height="20rem" />
        </div>
      ) : (
        data &&
        summary && (
          <>
            <EstateSummary summary={summary} />

            {/* The one filter row (rules §4.3) — scopes the heat rows, the
                latency trend and the site table below. */}
            <FilterRow
              presets={HISTORY_PRESETS}
              allowCustomRange={false}
              defaultPreset={DEFAULT_HISTORY_PRESET}
              dimensions={[
                {
                  param: SITE_FILTER_PARAM,
                  label: "Site",
                  noun: "site",
                  options: sites.map((site) => ({ value: site.id, label: site.name })),
                },
              ]}
            />

            <section id="history" aria-label="Availability history">
              {history && <UptimeHeatmap history={history} sites={filteredHistorySites} />}
            </section>

            <section id="latency" aria-label="Response time trend">
              {history && <LatencyTrend history={history} selectedIds={selectedIds} />}
            </section>

            <section id="sites" aria-label="Monitored sites">
              <SiteTable sites={filteredSites} />
            </section>

            <SiteDrawer sites={sites} history={history} />
          </>
        )
      )}
    </main>
  );
}
